"use client";

import { Icon } from "@iconify/react";
import { useEffect, useMemo, useRef } from "react";
import { ConversationCreditsExplainer } from "@/components/ConversationCreditsExplainer";

export type UpgradePlan = "starter" | "pro" | "recruiting";
export type CurrentPlan = "free" | UpgradePlan;

type PlanDetails = {
  name: string;
  priceLabel: string;
  badge?: string;
  accentClassName: string;
  features: string[];
  cta: string;
};

const PLAN_DETAILS: Record<UpgradePlan, PlanDetails> = {
  starter: {
    name: "Starter",
    priceLabel: "$25/month",
    accentClassName: "text-orange-600",
    features: [
      "10 conversation credits per month",
      "Unlimited AI Proxies",
      "Up to 30-minute conversations",
      "Transcripts + AI summaries",
      "Group Interviews",
    ],
    cta: "Continue with Starter",
  },
  pro: {
    name: "Pro",
    priceLabel: "$50/month",
    badge: "Most Popular",
    accentClassName: "text-[#5B8CFF]",
    features: [
      "30 conversation credits per month",
      "Unlimited AI Proxies",
      "Up to 30-minute conversations",
      "Transcripts + AI summaries",
      "Group Interviews",
    ],
    cta: "Continue with Pro",
  },
  recruiting: {
    name: "Recruiting",
    priceLabel: "$99/month",
    accentClassName: "text-emerald-700",
    features: [
      "100 conversation credits per month",
      "Unlimited candidate interview Proxies",
      "Up to 30-minute candidate interviews",
      "Candidate summaries + transcripts",
      "Group Interviews",
    ],
    cta: "Start Recruiting",
  },
};

const PLAN_ORDER: UpgradePlan[] = ["starter", "pro", "recruiting"];

export function UpgradeModal(props: {
  open: boolean;
  currentPlan?: CurrentPlan;
  loadingPlan?: UpgradePlan | null;
  reason?: string | null;
  onClose: () => void;
  onSelectPlan: (plan: UpgradePlan) => void;
}) {
  const { open, currentPlan, loadingPlan, reason, onClose, onSelectPlan } = props;
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const isBusy = loadingPlan !== null && loadingPlan !== undefined;

  const title = useMemo(() => {
    if (currentPlan === "recruiting") return "Switch plans";
    if (currentPlan === "starter" || currentPlan === "pro") return "Upgrade or switch plans";
    return "Choose your plan";
  }, [currentPlan]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && !isBusy) onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    window.setTimeout(() => {
      dialogRef.current?.querySelector<HTMLElement>("[data-upgrade-primary]")?.focus();
    }, 0);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, isBusy]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-label="Upgrade plan"
      onMouseDown={(e) => {
        if (isBusy) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />

      <div
        ref={dialogRef}
        className="relative w-full max-w-3xl rounded-3xl border border-stone-200 bg-white shadow-[0_24px_70px_rgba(0,0,0,0.25)] max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-4 p-6 border-b border-stone-100">
          <div>
            <h3 className="text-lg font-medium tracking-tight text-stone-900">{title}</h3>
            <p className="mt-1 text-sm text-stone-500">
              {reason || "Select a plan to continue to secure checkout."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            className="rounded-xl border border-stone-200 bg-white p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <Icon icon="solar:close-circle-linear" className="text-xl" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {PLAN_ORDER.map((plan) => {
              const details = PLAN_DETAILS[plan];
              const isCurrent = currentPlan === plan;
              const isLoading = loadingPlan === plan;
              const disabled = isBusy || isCurrent;

              return (
                <button
                  key={plan}
                  type="button"
                  data-upgrade-primary={plan === "pro" ? "true" : undefined}
                  disabled={disabled}
                  onClick={() => onSelectPlan(plan)}
                  className={[
                    "relative text-left rounded-2xl border p-5 transition-all shadow-sm",
                    plan === "pro"
                      ? "border-[#5B8CFF]/40 hover:border-[#5B8CFF]/70"
                      : plan === "recruiting"
                        ? "border-emerald-200 hover:border-emerald-300"
                        : "border-stone-200 hover:border-stone-300",
                    disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-stone-50/50",
                  ].join(" ")}
                >
                  {details.badge && (
                    <span className="absolute -top-2.5 right-4 inline-flex items-center rounded-full bg-[#5B8CFF] px-2.5 py-1 text-[11px] font-semibold text-white uppercase tracking-wider shadow-sm">
                      {details.badge}
                    </span>
                  )}

                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-medium tracking-tight text-stone-900">
                          {details.name}
                        </span>
                        {isCurrent ? (
                          <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] font-semibold text-stone-600">
                            Current
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 text-sm font-medium text-stone-700">
                        <span className={details.accentClassName}>{details.priceLabel}</span>
                      </div>
                    </div>
                    <div className="mt-0.5">
                      {isLoading ? (
                        <Icon icon="solar:spinner-linear" className="text-lg text-stone-500 animate-spin" />
                      ) : (
                        <Icon icon="solar:arrow-right-linear" className="text-lg text-stone-400" />
                      )}
                    </div>
                  </div>

                  <ul className="mt-4 space-y-2 text-sm text-stone-600">
                    {details.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Icon icon="solar:check-read-linear" className="mt-0.5 text-base text-stone-400 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5">
                    <span
                      className={[
                        "inline-flex w-full items-center justify-center rounded-xl px-3 py-2 text-sm font-medium transition-colors",
                        plan === "pro"
                          ? "bg-stone-900 text-white hover:bg-stone-800"
                          : plan === "recruiting"
                            ? "bg-emerald-700 text-white hover:bg-emerald-800"
                            : "bg-white border border-stone-200 text-stone-900 hover:bg-stone-50",
                        disabled ? "pointer-events-none" : "",
                      ].join(" ")}
                    >
                      {isCurrent
                        ? "Current plan"
                        : isLoading
                          ? "Redirecting…"
                          : details.cta}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          <ConversationCreditsExplainer compact className="mt-5" />

          <p className="mt-4 text-xs text-stone-400">
            You&apos;ll be redirected to LemonSqueezy checkout to complete payment.
          </p>
        </div>
      </div>
    </div>
  );
}
